import { format, parseISO } from 'date-fns';
import { addCalendarDays, diffCalendarDays, toLocalDateString } from '@/utils/dateHelpers';
import type { ReportPeriod } from './date-range/reportPeriod';
import { countBusinessDays, isBusinessDay } from './components/pacingReportModel';
import {
  getCanonicalEventDate,
  getCanonicalEventDates,
  isDateKeyWithinInclusiveRange,
  toPacificDateKey
} from './reporting';
import { getBorrowerName, getLoanOfficerName, type PipelineLoan } from './types';
import {
  PERFORMANCE_METRIC_KEYS,
  type CohortBasis,
  type CohortFolderValue,
  type CohortStageValue,
  type PerformanceAggregate,
  type PerformanceCohortRow,
  type PerformanceCohortSeries,
  type PerformanceLoanDetail,
  type PerformanceMetricKey,
  type PerformancePeriodCell,
  type PerformancePeriodRow,
  type PerformanceRange,
  type PerformanceTrendPoint,
  type PipelinePerformanceViewModel
} from './pipeline-performance/types';

interface DateWindow {
  from: string;
  to: string;
}

interface DateBucket extends DateWindow {
  key: string;
  label: string;
}

const EMPTY_AGGREGATE: PerformanceAggregate = {
  units: 0,
  volume: 0,
  unitsPerDay: 0,
  volumePerDay: 0
};

function dateAtNoon(dateKey: string): Date {
  return parseISO(`${dateKey}T12:00:00`);
}

function formatDateKey(dateKey: string): string {
  return format(dateAtNoon(dateKey), 'MMM d, yyyy');
}

function formatWindow(window: DateWindow): string {
  return `${formatDateKey(window.from)} – ${formatDateKey(window.to)}`;
}

function monthStart(dateKey: string): string {
  return `${dateKey.slice(0, 7)}-01`;
}

function monthEnd(dateKey: string): string {
  const [year, month] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
}

function shiftMonths(dateKey: string, months: number): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(Date.UTC(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth() + 1,
    0
  )).getUTCDate();
  shifted.setUTCDate(Math.min(day, lastDay));
  return shifted.toISOString().slice(0, 10);
}

function shiftYears(dateKey: string, years: number): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year + years, month, 0)).getUTCDate();
  return new Date(Date.UTC(year + years, month - 1, Math.min(day, lastDay)))
    .toISOString()
    .slice(0, 10);
}

function trailingNonBusinessDays(window: DateWindow): number {
  let cursor = window.to;
  let days = 0;
  while (cursor >= window.from && !isBusinessDay(cursor)) {
    days += 1;
    cursor = addCalendarDays(cursor, -1);
  }
  return days;
}

function endAfterBusinessDays(
  from: string,
  naturalPeriodEnd: string,
  businessDays: number,
  trailingDays: number
): string {
  if (businessDays === 0) {
    return addCalendarDays(from, -1);
  }

  let seen = 0;
  let cursor = from;
  while (cursor <= naturalPeriodEnd) {
    if (isBusinessDay(cursor)) {
      seen += 1;
      if (seen === businessDays) {
        let end = cursor;
        for (let i = 0; i < trailingDays; i++) {
          const next = addCalendarDays(end, 1);
          if (next > naturalPeriodEnd || isBusinessDay(next)) {
            break;
          }
          end = next;
        }
        return end;
      }
    }
    cursor = addCalendarDays(cursor, 1);
  }
  return naturalPeriodEnd;
}

export function getPerformanceComparisonWindow(
  period: ReportPeriod,
  current: DateWindow
): DateWindow {
  const currentBusinessDays = countBusinessDays(current.from, current.to);
  const trailingDays = trailingNonBusinessDays(current);

  if (period.mode === 'mtd') {
    const from = monthStart(shiftMonths(current.from, -1));
    return {
      from,
      to: endAfterBusinessDays(from, monthEnd(from), currentBusinessDays, trailingDays)
    };
  }

  if (period.mode === 'qtd') {
    const from = shiftMonths(current.from, -3);
    const naturalEnd = addCalendarDays(shiftMonths(current.from, 0), -1);
    return {
      from,
      to: endAfterBusinessDays(from, naturalEnd, currentBusinessDays, trailingDays)
    };
  }

  if (period.mode === 'ytd') {
    const from = shiftYears(current.from, -1);
    const naturalEnd = `${from.slice(0, 4)}-12-31`;
    return {
      from,
      to: endAfterBusinessDays(from, naturalEnd, currentBusinessDays, trailingDays)
    };
  }

  if (period.mode === 'lastMonth') {
    const from = monthStart(shiftMonths(current.from, -1));
    return { from, to: monthEnd(from) };
  }

  if (period.mode === 'lastQuarter') {
    return {
      from: shiftMonths(current.from, -3),
      to: addCalendarDays(current.from, -1)
    };
  }

  if (period.mode === 'lastYear') {
    return {
      from: shiftYears(current.from, -1),
      to: shiftYears(current.to, -1)
    };
  }

  const calendarDays = Math.max(1, diffCalendarDays(current.from, current.to) + 1);
  const to = addCalendarDays(current.from, -1);
  return {
    from: addCalendarDays(to, -(calendarDays - 1)),
    to
  };
}

function toPerformanceRange(window: DateWindow, today: string): PerformanceRange {
  const isEmpty = window.to < window.from;
  return {
    ...window,
    label: isEmpty ? 'Available after the first business day' : formatWindow(window),
    businessDays: isEmpty ? 0 : countBusinessDays(window.from, window.to),
    includesToday: !isEmpty && window.from <= today && today <= window.to,
    isEmpty
  };
}

function makeAggregate(units: number, volume: number, businessDays: number): PerformanceAggregate {
  return {
    units,
    volume,
    unitsPerDay: businessDays > 0 ? units / businessDays : 0,
    volumePerDay: businessDays > 0 ? volume / businessDays : 0
  };
}

function deltaPct(current: number, comparison: number): number | null {
  if (!Number.isFinite(comparison) || comparison === 0) {
    return null;
  }
  return ((current - comparison) / comparison) * 100;
}

function loanDetail(
  loan: PipelineLoan,
  eventDate: string
): PerformanceLoanDetail {
  const dates = getCanonicalEventDates(loan);
  return {
    id: loan.encompass_loan_id,
    loanNumber: loan.trans_details_loan_number || '—',
    borrower: getBorrowerName(loan),
    loanOfficer: getLoanOfficerName(loan),
    eventDate,
    amount: Number(loan.trans_details_loan_amt) || 0,
    folder: loan.loan_folder || 'Unknown',
    channel: loan.channel || 'Unknown',
    createdDate: dates.created,
    applicationDate: dates.application,
    lockDate: dates.locked,
    fundedDate: dates.funded,
    investor: loan.investor_name,
    product: loan.rate_lock_loan_program
  };
}

function eventDetails(
  loans: PipelineLoan[],
  metric: PerformanceMetricKey,
  window: DateWindow
): PerformanceLoanDetail[] {
  return loans
    .map((loan) => {
      const eventDate = getCanonicalEventDate(loan, metric);
      return eventDate && isDateKeyWithinInclusiveRange(eventDate, window.from, window.to)
        ? loanDetail(loan, eventDate)
        : null;
    })
    .filter((detail): detail is PerformanceLoanDetail => detail !== null)
    .sort((a, b) => b.eventDate.localeCompare(a.eventDate) || a.loanNumber.localeCompare(b.loanNumber));
}

function aggregateDetails(
  details: PerformanceLoanDetail[],
  businessDays: number
): PerformanceAggregate {
  return makeAggregate(
    details.length,
    details.reduce((sum, detail) => sum + detail.amount, 0),
    businessDays
  );
}

function emptyMetricCells(): Record<PerformanceMetricKey, PerformancePeriodCell> {
  return {
    created: { units: 0, volume: 0, loans: [] },
    application: { units: 0, volume: 0, loans: [] },
    locked: { units: 0, volume: 0, loans: [] },
    funded: { units: 0, volume: 0, loans: [] }
  };
}

function rangeLength(window: DateWindow): number {
  return Math.max(1, diffCalendarDays(window.from, window.to) + 1);
}

function dailyBuckets(window: DateWindow): DateBucket[] {
  const buckets: DateBucket[] = [];
  let cursor = window.from;
  while (cursor <= window.to) {
    buckets.push({
      key: cursor,
      label: format(dateAtNoon(cursor), 'MMM d'),
      from: cursor,
      to: cursor
    });
    cursor = addCalendarDays(cursor, 1);
  }
  return buckets;
}

function weeklyBuckets(window: DateWindow): DateBucket[] {
  const buckets: DateBucket[] = [];
  let cursor = window.from;
  let index = 1;
  while (cursor <= window.to) {
    const to = [addCalendarDays(cursor, 6), window.to].sort()[0];
    buckets.push({
      key: `week-${index}`,
      label: `${format(dateAtNoon(cursor), 'MMM d')}–${format(dateAtNoon(to), 'MMM d')}`,
      from: cursor,
      to
    });
    cursor = addCalendarDays(to, 1);
    index += 1;
  }
  return buckets;
}

function monthlyBuckets(window: DateWindow): DateBucket[] {
  const buckets: DateBucket[] = [];
  let cursor = window.from;
  while (cursor <= window.to) {
    const key = cursor.slice(0, 7);
    const to = [monthEnd(cursor), window.to].sort()[0];
    buckets.push({
      key,
      label: format(dateAtNoon(`${key}-01`), 'MMM yyyy'),
      from: cursor,
      to
    });
    cursor = addCalendarDays(to, 1);
  }
  return buckets;
}

export function buildPerformanceBuckets(window: DateWindow): DateBucket[] {
  const days = rangeLength(window);
  if (days <= 31) {
    return dailyBuckets(window);
  }
  if (days <= 180) {
    return weeklyBuckets(window);
  }
  return monthlyBuckets(window);
}

function aggregateBucket(
  loans: PipelineLoan[],
  metric: PerformanceMetricKey,
  bucket: DateBucket
): PerformanceAggregate {
  return aggregateDetails(
    eventDetails(loans, metric, bucket),
    countBusinessDays(bucket.from, bucket.to)
  );
}

function periodRow(loans: PipelineLoan[], bucket: DateBucket): PerformancePeriodRow {
  const metrics = emptyMetricCells();
  for (const metric of PERFORMANCE_METRIC_KEYS) {
    const details = eventDetails(loans, metric, bucket);
    metrics[metric] = {
      units: details.length,
      volume: details.reduce((sum, detail) => sum + detail.amount, 0),
      loans: details
    };
  }
  return {
    key: bucket.key,
    label: bucket.label,
    businessDays: countBusinessDays(bucket.from, bucket.to),
    metrics
  };
}

function stageValue(
  details: PerformanceLoanDetail[],
  cohortUnits: number
): CohortStageValue {
  const volume = details.reduce((sum, detail) => sum + detail.amount, 0);
  return {
    units: details.length,
    volume,
    percentage: cohortUnits > 0 ? (details.length / cohortUnits) * 100 : 0,
    loans: details
  };
}

function cohortSeries(
  loans: PipelineLoan[],
  basis: CohortBasis,
  buckets: DateBucket[],
  asOf: string
): PerformanceCohortSeries {
  const rows: PerformanceCohortRow[] = [];
  const allCohortLoans = new Map<string, { loan: PipelineLoan; date: string }>();
  let totalAnomalies = 0;

  for (const bucket of buckets) {
    const cohortLoans = loans
      .map((loan) => ({ loan, date: getCanonicalEventDate(loan, basis) }))
      .filter((item): item is { loan: PipelineLoan; date: string } =>
        Boolean(item.date && isDateKeyWithinInclusiveRange(item.date, bucket.from, bucket.to))
      );
    const cohortDetails = cohortLoans.map(({ loan, date }) => loanDetail(loan, date));
    const downstream: Record<'application' | 'locked' | 'funded', PerformanceLoanDetail[]> = {
      application: [],
      locked: [],
      funded: []
    };
    let anomalies = 0;

    cohortLoans.forEach(({ loan, date: cohortDate }) => {
      allCohortLoans.set(loan.encompass_loan_id, { loan, date: cohortDate });
      (['application', 'locked', 'funded'] as const).forEach((stage) => {
        const stageDate = getCanonicalEventDate(loan, stage);
        if (!stageDate || stageDate > asOf) {
          return;
        }
        if (stageDate < cohortDate) {
          anomalies += 1;
          return;
        }
        downstream[stage].push(loanDetail(loan, stageDate));
      });
    });

    if (basis === 'application') {
      downstream.application = cohortDetails;
    }
    totalAnomalies += anomalies;
    rows.push({
      key: bucket.key,
      label: bucket.label,
      cohort: stageValue(cohortDetails, cohortDetails.length),
      application: stageValue(downstream.application, cohortDetails.length),
      locked: stageValue(downstream.locked, cohortDetails.length),
      funded: stageValue(downstream.funded, cohortDetails.length),
      anomalyCount: anomalies
    });
  }

  const folderGroups = new Map<string, PerformanceLoanDetail[]>();
  allCohortLoans.forEach(({ loan, date }) => {
    const folder = loan.loan_folder || 'Unknown';
    const group = folderGroups.get(folder) || [];
    group.push(loanDetail(loan, date));
    folderGroups.set(folder, group);
  });
  const cohortUnits = allCohortLoans.size;
  const folderDistribution: CohortFolderValue[] = Array.from(folderGroups.entries())
    .map(([folder, details]) => ({
      folder,
      units: details.length,
      volume: details.reduce((sum, detail) => sum + detail.amount, 0),
      percentage: cohortUnits > 0 ? (details.length / cohortUnits) * 100 : 0,
      loans: details
    }))
    .sort((a, b) => b.units - a.units || a.folder.localeCompare(b.folder));

  return {
    rows: [...rows].reverse(),
    folderDistribution,
    anomalyCount: totalAnomalies
  };
}

export function buildPipelinePerformanceModel(
  loans: PipelineLoan[],
  period: ReportPeriod,
  now: Date = new Date()
): PipelinePerformanceViewModel {
  const today = toPacificDateKey(now) || toLocalDateString(now);
  const selectedFrom = toLocalDateString(period.from);
  const selectedTo = toLocalDateString(period.to);
  const current: DateWindow = {
    from: selectedFrom,
    to: selectedTo > today ? today : selectedTo
  };

  if (current.to < current.from) {
    current.to = current.from;
  }

  const comparison = getPerformanceComparisonWindow(period, current);
  const currentRange = toPerformanceRange(current, today);
  const comparisonRange = toPerformanceRange(comparison, today);
  const summaries = {} as PipelinePerformanceViewModel['summaries'];

  for (const metric of PERFORMANCE_METRIC_KEYS) {
    const currentDetails = eventDetails(loans, metric, current);
    const comparisonDetails = eventDetails(loans, metric, comparison);
    const currentAggregate = aggregateDetails(currentDetails, currentRange.businessDays);
    const comparisonAggregate = aggregateDetails(comparisonDetails, comparisonRange.businessDays);
    summaries[metric] = {
      current: currentAggregate,
      comparison: comparisonAggregate,
      unitsPerDayDeltaPct: deltaPct(
        currentAggregate.unitsPerDay,
        comparisonAggregate.unitsPerDay
      ),
      volumePerDayDeltaPct: deltaPct(
        currentAggregate.volumePerDay,
        comparisonAggregate.volumePerDay
      ),
      loans: currentDetails
    };
  }

  const currentBuckets = buildPerformanceBuckets(current);
  const comparisonBuckets = buildPerformanceBuckets(comparison);
  const trendLength = Math.max(currentBuckets.length, comparisonBuckets.length);
  const trend: PerformanceTrendPoint[] = Array.from({ length: trendLength }, (_, index) => {
    const bucket = currentBuckets[index];
    const comparisonBucket = comparisonBuckets[index];
    const metrics = {} as PerformanceTrendPoint['metrics'];
    for (const metric of PERFORMANCE_METRIC_KEYS) {
      metrics[metric] = {
        current: bucket
          ? aggregateBucket(loans, metric, bucket)
          : EMPTY_AGGREGATE,
        comparison: comparisonBucket
          ? aggregateBucket(loans, metric, comparisonBucket)
          : EMPTY_AGGREGATE
      };
    }
    return {
      key: bucket?.key || `comparison-${comparisonBucket?.key || index}`,
      label: bucket?.label || '—',
      comparisonLabel: comparisonBucket?.label || '—',
      hasCurrent: Boolean(bucket),
      hasComparison: Boolean(comparisonBucket),
      metrics
    };
  });

  const periods = currentBuckets.map((bucket) => periodRow(loans, bucket)).reverse();
  const applicationCohorts = cohortSeries(loans, 'application', currentBuckets, today);
  const createdCohorts = cohortSeries(loans, 'created', currentBuckets, today);
  const anomalyCount = applicationCohorts.anomalyCount + createdCohorts.anomalyCount;
  const warnings = anomalyCount > 0
    ? [`${anomalyCount} downstream cohort event${anomalyCount === 1 ? '' : 's'} occurred before the cohort date and were excluded.`]
    : [];

  return {
    currentRange,
    comparisonRange,
    summaries,
    trend,
    periods,
    cohorts: {
      application: applicationCohorts,
      created: createdCohorts
    },
    warnings
  };
}
